import type { MouseEvent } from "react";
import { createPortal } from "react-dom";
import { t } from "@/i18n-js/instance";
import type { PaywallPrice } from "@/react/types/PaywallPrice";
import { FreeTrial } from "./FreeTrial";
import { PaywallPriceElement } from "./PaywallPriceElement";
import { addButtonLocked } from "./addButtonLocked";
import { INTERVAL_OPTIONS } from "./constants";
import { usePaywallPrices } from "./hooks/usePaywallPrices";

interface PaywallPricesProps {
  name: string;
  paywall_prices: PaywallPrice[];
  prices_max_count: number;
  trial_days?: number;
}

export const PaywallPrices = ({
  name,
  paywall_prices: paywallPrices,
  prices_max_count: pricesMaxCount,
  trial_days: trialDays,
}: PaywallPricesProps) => {
  const {
    currentPaywallPrices,
    intervalOption,
    onIntervalOptionChange,
    amount,
    onAmountChange,
    onAdd,
    onRemove,
    onSetPreferred,
    changes,
    hasSubscriptionPrices,
  } = usePaywallPrices(paywallPrices, pricesMaxCount);

  const container = document.getElementById("paywall-free-trial-container");

  const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
    onAdd(event);
  };

  return (
    <div className="mt-2.5">
      <div className="mb-2.5 flex" data-testid="add-new-price">
        <select
          className="mr-1.5"
          value={intervalOption}
          onChange={onIntervalOptionChange}
        >
          {INTERVAL_OPTIONS.map(option => (
            <option key={option} value={option}>
              {t(
                `settings.paywall_prices.form.interval_select_options.${option}_label`,
              )}
            </option>
          ))}
        </select>
        <span className="border-secondary bg-secondary text-tertiary inline-flex items-center rounded-l-md border border-r-0 px-3 text-sm">
          $
        </span>
        <input
          type="number"
          step=".01"
          min="1"
          placeholder={t("settings.paywall_prices.amount")}
          className="form-control w-25 mr-1.5 rounded-none rounded-r-md pr-0 [appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none"
          value={amount}
          onChange={onAmountChange}
        />
        <button
          type="submit"
          disabled={addButtonLocked(
            amount,
            intervalOption,
            currentPaywallPrices,
            pricesMaxCount,
          )}
          className="btn btn-circle !mt-0"
          onClick={handleAdd}
        >
          {t("settings.paywall_prices.buttons.add")}
        </button>
      </div>
      <div className="bg-secondary flex flex-col items-stretch rounded-md">
        {currentPaywallPrices.map(
          (
            {
              id,
              tempId,
              price_type,
              interval,
              interval_count,
              interval_label,
              amount: priceAmount,
              preferred,
            },
            index,
          ) => (
            <PaywallPriceElement
              key={id || tempId}
              priceType={price_type}
              interval={interval}
              intervalLabel={interval_label}
              intervalCount={interval_count}
              amount={priceAmount}
              preferred={preferred}
              allowPreferredAction={currentPaywallPrices.length > 1}
              onRemove={() => onRemove(index)}
              onSetPreferred={() => onSetPreferred(index)}
            />
          ),
        )}
      </div>
      {changes.map(change => (
        <input key={change} name={`${name}[]`} type="hidden" value={change} />
      ))}
      {container &&
        createPortal(
          <FreeTrial
            trialDays={trialDays}
            hasSubscriptionPrices={hasSubscriptionPrices}
          />,
          container,
        )}
    </div>
  );
};
